import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AccountRestore — landing page for the "undo deletion" link emailed to a
 * user when they queue their account for deletion. Validates the signed
 * cancel-token via POST /api/account/delete/cancel and shows the outcome.
 *
 * Public route (no auth) — token is the auth.
 */
const AccountRestore = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ status: 'pending', message: '' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'Missing restore token. Please use the link from your deletion-confirmation email.' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/account/delete/cancel`, { token });
        if (cancelled) return;
        setState({ status: 'ok', message: r.data?.message || 'Your account has been restored.' });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err?.response?.data?.detail || 'Could not restore the account.',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-enf-surface-base text-enf-text-primary flex items-center justify-center px-6">
      <div
        className="max-w-md w-full bg-enf-surface-deep border border-enf-surface-border rounded-xl p-8 text-center"
        data-testid="account-restore-card"
      >
        {state.status === 'pending' && (
          <>
            <Loader2 className="w-10 h-10 text-enf-accent-fg animate-spin mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-wide mb-2">Restoring your account…</h1>
            <p className="text-sm text-enf-text-secondary">One moment.</p>
          </>
        )}
        {state.status === 'ok' && (
          <>
            <CheckCircle className="w-10 h-10 text-enf-role-success mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-wide mb-2" data-testid="account-restore-success">Welcome back.</h1>
            <p className="text-sm text-enf-text-secondary mb-6">{state.message}</p>
            <Link
              to="/login"
              className="inline-block px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
              data-testid="account-restore-login-cta"
            >
              Sign in
            </Link>
          </>
        )}
        {state.status === 'error' && (
          <>
            <AlertCircle className="w-10 h-10 text-enf-role-warning mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-wide mb-2" data-testid="account-restore-error">Restore failed</h1>
            <p className="text-sm text-enf-text-secondary mb-6">{state.message}</p>
            <p className="text-xs text-enf-text-muted mb-4">
              If your 7-day grace period has elapsed, your account and data have been permanently deleted.
              Otherwise, the link may have been used or be malformed.
            </p>
            <p className="text-xs text-enf-text-muted">
              Need help? Email <a href="mailto:privacy@enferno.app" className="text-enf-accent-fg hover:underline">privacy@enferno.app</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountRestore;
